import type { Feature, FeatureCollection } from "geojson";
import type { ComponentType } from "react";

import type { OpoContext, OpoDraft } from "../../api/types";
import { graphicPoints } from "../../app/home/opoDraft";
import { revealPaint, useFadeIn } from "../reveal";
import { FRIEND_BLUE, HOSTILE_RED } from "../units/symbols";

/** Couches de la carte d'aperçu de la vérification d'un OPO (docs/20 §12), au-dessus du fond et des couches du
 *  camp : limites du fuseau en bleu ami (trait plein : mesure de l'ordre ; tireté : fuseau automatique du camp),
 *  LIMA en bleu ami, unités au temps choisi en points bleu ami (APP-6 : la couleur dit le camp) et rouge ennemi,
 *  lieux résolus en anneaux gris, lieu choisi (ou survolé dans la liste) en anneau d'action avec son nom et ses
 *  homonymes en anneaux numérotés ; étiquettes des unités et des LIMA en couches de texte, masquées quand elles se
 *  chevauchent. Limites entre unités lues dans le texte en tireté bleu ami, graphiques nommés (itinéraires, axes en
 *  trait bleu ami ; zones d'engagement, objectifs, zones d'intérêt en anneaux) étiquetés de leur nom (docs/20 §14.4).
 *  Entrée en fondu (300 ms). Les poignées de tracé sont des repères HTML (`VerificationMap`). */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
}

const FADE_MS = 300;
const FONT = ["Open Sans Semibold"];
// Étiquettes de la carte d'aperçu : masquées quand elles se chevauchent (lisibles au cadrage du camp entier).
const LABEL_LAYOUT = { "text-font": FONT, "text-size": 11, "text-allow-overlap": false, "text-optional": true, "text-max-width": 10 };
const LABEL_PAINT = { "text-color": "#1f2430", "text-halo-color": "#ffffff", "text-halo-width": 1.4 };
const PLACE_GREY = "#5b6170";
const ACTION_BLUE = "#2f6fed"; // jeton accent
const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };
const collection = (features: Feature[]): FeatureCollection => ({ type: "FeatureCollection", features });
const line = (coordinates: [number, number][], properties: Record<string, unknown> = {}): Feature => ({
  type: "Feature",
  geometry: { type: "LineString", coordinates },
  properties,
});
const point = (at: [number, number], properties: Record<string, unknown> = {}): Feature => ({
  type: "Feature",
  geometry: { type: "Point", coordinates: at },
  properties,
});

/** Fond de la carte d'aperçu sans jeton Mapbox : celui du plan de l'iPad (comme la carte du dossier). */
export function verificationBaseStyle(glyphs: string) {
  return {
    version: 8 as const,
    glyphs,
    sources: {},
    layers: [{ id: "background", type: "background" as const, paint: { "background-color": "#e8e8e8" } }],
  };
}

export function VerificationLayers({
  Source,
  Layer,
  draft,
  context,
  phase,
  selectedPlace,
  showPlaces = true,
}: EngineParts & {
  draft: OpoDraft;
  context: OpoContext;
  phase: "T1" | "T2" | "T3";
  selectedPlace: string | null;
  /** Lieux résolus (masqués pendant le tracé : leurs anneaux se confondraient avec les poignées). */
  showPlaces?: boolean;
}) {
  const corridor = draft.corridor;
  const custom = corridor.mode === "custom";
  const rails: Feature[] = [];
  if (corridor.left.length >= 2) rails.push(line(corridor.left, { custom }));
  if (corridor.right.length >= 2) rails.push(line(corridor.right, { custom }));
  const limas: Feature[] = [];
  for (const [name, coords] of Object.entries(corridor.lines)) {
    if (coords && coords.length >= 2) limas.push(line(coords, { name }));
  }
  // LIMA au-delà de la quatrième : graphiques de carte, en tireté.
  const extras = draft.lima.flatMap((l) => (l.slot === null && l.points.length >= 2 ? [line(l.points, { name: l.name })] : []));
  const units = (context.map.units ?? []).flatMap((u) => {
    const at = u.positions[phase]?.point;
    return at ? [point(at, { side: u.side, label: u.label })] : [];
  });
  const places = showPlaces
    ? draft.places.flatMap((p) => (p.point ? [point(p.point, { selected: p.id === selectedPlace, label: p.text || p.coordinate })] : []))
    : [];
  // Homonymes du lieu choisi : anneaux numérotés comme la liste (1, 2, 3).
  const chosen = showPlaces ? draft.places.find((p) => p.id === selectedPlace) : undefined;
  const candidates = chosen && chosen.candidates.length > 1 ? chosen.candidates.map((c, i) => point(c.point, { n: String(i + 1) })) : [];
  // Limites entre unités et graphiques nommés lus (gardés seulement).
  const boundaries = (draft.boundaries ?? []).flatMap((b) =>
    b.status === "keep" && b.points.length >= 2 ? [line(b.points, { label: b.labels.join(" / ") })] : [],
  );
  const graphicLines: Feature[] = [];
  const graphicSpots: Feature[] = [];
  for (const g of draft.graphics ?? []) {
    if (g.status !== "keep") continue;
    const pts = graphicPoints(draft, g);
    if ((g.kind === "route" || g.kind === "axis") && pts.length >= 2) graphicLines.push(line(pts, { label: g.name }));
    else if (pts.length > 0) {
      const at: [number, number] = [pts.reduce((a, p) => a + p[0], 0) / pts.length, pts.reduce((a, p) => a + p[1], 0) / pts.length];
      graphicSpots.push(point(at, { label: g.name }));
    }
  }
  const limaTags = Object.entries(corridor.lines).flatMap(([name, coords]) => (coords && coords.length >= 2 ? [point(coords[0]!, { name })] : []));
  const on = useFadeIn(`${draft.camp ?? context.map.terrain?.slug ?? ""}-${phase}`);
  return (
    <>
      <Source id="gx-verif-rails" type="geojson" data={rails.length ? collection(rails) : EMPTY}>
        <Layer
          id="verif-rails"
          type="line"
          layout={{ "line-join": "round", "line-cap": "round" }}
          // Clé du tiret dans l'identifiant : Mapbox GL ne change pas un `line-dasharray` en place.
          key={custom ? "custom" : "camp"}
          paint={revealPaint("line", { "line-color": FRIEND_BLUE, "line-width": 2.5, ...(custom ? {} : { "line-dasharray": [3, 2] }) }, on, FADE_MS)}
        />
      </Source>
      <Source id="gx-verif-lima" type="geojson" data={limas.length ? collection(limas) : EMPTY}>
        <Layer id="verif-lima" type="line" paint={revealPaint("line", { "line-color": FRIEND_BLUE, "line-width": 1.8 }, on, FADE_MS)} />
      </Source>
      <Source id="gx-verif-lima-extra" type="geojson" data={extras.length ? collection(extras) : EMPTY}>
        <Layer
          id="verif-lima-extra"
          type="line"
          paint={revealPaint("line", { "line-color": FRIEND_BLUE, "line-width": 1.5, "line-dasharray": [2, 2] }, on, FADE_MS)}
        />
      </Source>
      <Source id="gx-verif-boundaries" type="geojson" data={boundaries.length ? collection(boundaries) : EMPTY}>
        <Layer
          id="verif-boundaries"
          type="line"
          paint={revealPaint("line", { "line-color": FRIEND_BLUE, "line-width": 1.6, "line-dasharray": [4, 2, 1, 2] }, on, FADE_MS)}
        />
      </Source>
      <Source id="gx-verif-graphic-lines" type="geojson" data={graphicLines.length ? collection(graphicLines) : EMPTY}>
        <Layer id="verif-graphic-lines" type="line" paint={revealPaint("line", { "line-color": FRIEND_BLUE, "line-width": 1.6 }, on, FADE_MS)} />
        <Layer
          id="verif-graphic-lines-label"
          type="symbol"
          layout={{ ...LABEL_LAYOUT, "symbol-placement": "line", "text-field": ["get", "label"] }}
          paint={revealPaint("symbol", { ...LABEL_PAINT, "text-color": FRIEND_BLUE }, on, FADE_MS)}
        />
      </Source>
      <Source id="gx-verif-graphic-spots" type="geojson" data={graphicSpots.length ? collection(graphicSpots) : EMPTY}>
        <Layer
          id="verif-graphic-spots"
          type="circle"
          paint={revealPaint(
            "circle",
            { "circle-radius": 11, "circle-color": "#ffffff", "circle-opacity": 0.4, "circle-stroke-color": FRIEND_BLUE, "circle-stroke-width": 1.6 },
            on,
            FADE_MS,
          )}
        />
        <Layer
          id="verif-graphic-spots-label"
          type="symbol"
          layout={{ ...LABEL_LAYOUT, "text-field": ["get", "label"], "text-anchor": "top", "text-offset": [0, 1.1] }}
          paint={revealPaint("symbol", { ...LABEL_PAINT, "text-color": FRIEND_BLUE }, on, FADE_MS)}
        />
      </Source>
      <Source id="gx-verif-places" type="geojson" data={places.length ? collection(places) : EMPTY}>
        <Layer
          id="verif-places"
          type="circle"
          paint={revealPaint(
            "circle",
            {
              "circle-radius": ["case", ["==", ["get", "selected"], true], 9, 5],
              "circle-color": "#ffffff",
              "circle-opacity": 0.6,
              "circle-stroke-color": ["case", ["==", ["get", "selected"], true], ACTION_BLUE, PLACE_GREY],
              "circle-stroke-width": ["case", ["==", ["get", "selected"], true], 3, 1.5],
            },
            on,
            FADE_MS,
          )}
        />
      </Source>
      <Source id="gx-verif-candidates" type="geojson" data={candidates.length ? collection(candidates) : EMPTY}>
        <Layer
          id="verif-candidates"
          type="circle"
          paint={revealPaint("circle", { "circle-radius": 8, "circle-color": "#ffffff", "circle-stroke-color": ACTION_BLUE, "circle-stroke-width": 2 }, on, FADE_MS)}
        />
        <Layer
          id="verif-candidates-n"
          type="symbol"
          layout={{ "text-field": ["get", "n"], "text-font": FONT, "text-size": 11, "text-allow-overlap": true }}
          paint={revealPaint("symbol", { "text-color": ACTION_BLUE }, on, FADE_MS)}
        />
      </Source>
      <Source id="gx-verif-units" type="geojson" data={units.length ? collection(units) : EMPTY}>
        <Layer
          id="verif-units"
          type="circle"
          paint={revealPaint(
            "circle",
            {
              "circle-radius": 6,
              "circle-color": ["case", ["==", ["get", "side"], "ENI"], HOSTILE_RED, FRIEND_BLUE],
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": 1.5,
            },
            on,
            FADE_MS,
          )}
        />
        {/* Étiquettes des unités : celles qui se chevauchent sont masquées (la plus haute dans la liste reste). */}
        <Layer
          id="verif-units-label"
          type="symbol"
          layout={{ ...LABEL_LAYOUT, "text-field": ["get", "label"], "text-anchor": "top", "text-offset": [0, 0.8] }}
          paint={revealPaint("symbol", LABEL_PAINT, on, FADE_MS)}
        />
      </Source>
      <Source id="gx-verif-lima-tags" type="geojson" data={limaTags.length ? collection(limaTags) : EMPTY}>
        <Layer
          id="verif-lima-tags"
          type="symbol"
          layout={{ ...LABEL_LAYOUT, "text-field": ["get", "name"], "text-anchor": "bottom", "text-offset": [0, -0.4] }}
          paint={revealPaint("symbol", { ...LABEL_PAINT, "text-color": FRIEND_BLUE }, on, FADE_MS)}
        />
      </Source>
      <Source id="gx-verif-place-tag" type="geojson" data={chosen?.point ? collection([point(chosen.point, { label: chosen.text || chosen.coordinate })]) : EMPTY}>
        <Layer
          id="verif-place-tag"
          type="symbol"
          layout={{ ...LABEL_LAYOUT, "text-field": ["get", "label"], "text-anchor": "bottom", "text-offset": [0, -0.9], "text-allow-overlap": true }}
          paint={revealPaint("symbol", { ...LABEL_PAINT, "text-color": ACTION_BLUE }, on, FADE_MS)}
        />
      </Source>
    </>
  );
}
